<script setup lang="ts">
/**
 * Singbac Incorporation (Pre) — 문서 [[Signers]] 자리 (MY SignersField 재사용).
 * DocScope(dir_{id}/ · sec_{id}/ · ctrl_{id}/) 안에서 쓰이며, 부수마다 독립 서명키
 * `${scope}Signers` 로 저장한다. 본인 성명(name-key)이 채워지면 서명자 1명으로 seed,
 * 사용자가 수정한 뒤에는 덮지 않는다. entityCd 는 넘기지 않음(Pre 단계 officer API 없음).
 */
import { computed, inject, watch } from 'vue';
import SignersField from '@/features/startTask/preset_task/my/preset/components/resignation/signers/SignersField.vue';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { DOC_SCOPE, useCddFill } from './cdd/useCddFill';
import { useSgIncPreSigners } from './cdd/sgIncPreSignersContext';

const props = withDefaults(
  defineProps<{
    /** 이 문서 본인 성명의 bare field-key (예: director_name / secretary_name / controller_name) */
    nameKey: string;
    /** nameKey 가 비었을 때 쓸 성명 키 — 패널 생성(signersForScope)의 이름 순서와 같게 맞춘다 */
    fallbackNameKey?: string;
    /** 서명자 직함 (Director / Secretary / Controller) */
    title?: string;
    boardLabel?: string;
  }>(),
  { title: 'Director', boardLabel: '', fallbackNameKey: '' },
);

const scope = inject(DOC_SCOPE, '');
const fill = useCddFill();
const ctx = useSgIncPreSigners();

const varKey = computed(() => `${scope}Signers`);
const signers = computed(() => ctx.signersOf(varKey.value));

/** 본인 성명 — 스코프 키 우선, 없으면 top-level. 비었으면 fallbackNameKey(같은 순서). */
const nameOf = (key: string) => (key ? String(fill.values[`${scope}${key}`] || fill.values[key] || '').trim() : '');
const personName = computed(() => nameOf(props.nameKey) || nameOf(props.fallbackNameKey));

// 성명이 채워지면(자동채움 포함) 서명자 1명으로 seed, 자동 seed 1명이면 성명 수정을 따라감. 사용자가 바꾼 서명자는 유지.
//   ctx.revision(): 작업 복원(importAll)이 store 를 비운 뒤에도 다시 seed — 예전엔 Edit 로 연 작업에서 서명 칸이 빈칸으로 남았다.
watch(
  [personName, varKey, () => ctx.revision()],
  () => ctx.seedIfEmpty(varKey.value, personName.value, props.title),
  { immediate: true },
);

function onUpdate(list: SignerEntry[]) {
  ctx.setSigners(varKey.value, list);
}
</script>

<template>
  <SignersField
    :model-value="signers"
    :board-label="boardLabel"
    :var-key="varKey"
    entity-cd=""
    @update:model-value="onUpdate"
  />
</template>
